import { AxiosError } from 'axios'
import cn from 'classnames'
import AlertPanel from 'ext-ui/components/AlertPanel/AlertPanel'
import BaseModal, {
  BaseModalProps,
} from 'ext-ui/components/BaseModal/BaseModal'
import Button from 'ext-ui/components/Button/Button'
import Checkbox from 'ext-ui/components/Checkbox/Checkbox'
import AlertTriangleIcon from 'ext-ui/svg/icon/AlertTriangleIcon'
import Markdown from 'markdown-to-jsx'
import React, {
  ChangeEvent,
  KeyboardEvent,
  ReactElement,
  useState,
} from 'react'
import { useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import { usePutCreditCardV2 } from 'src/api/mutation/creditcards'
import DiagonalCardsIllustration from 'src/assets/images/diagonal-cards-illustration.png'
import SvgChevronDown from 'src/components/Svgs/SvgChevronDown'
import SvgChevronUp from 'src/components/Svgs/SvgChevronUp'
import { useTranslate } from 'src/localization/Trns'
import { useQueryOrganizationFeatureFlags } from 'src/queries/organizations/featureflags.queries'
import { selectUserListModelFromState } from 'src/redux/selectors/auth.selectors'
import { Userpilot } from 'userpilot'

import style from './EnableVirtualCardsModal.style'

type EnableVirtualCardsModalProps = BaseModalProps & {
  creditCardId: string
  /** onEnableComplete sends the accountId of the newly created card/account */
  onEnableComplete?: (cardId: string) => void
  onEnableError?: () => void
}

/**
 * Allows a card to be enabled for virtual card by the cardId passed in as a prop.
 */
export default function EnableVirtualCardsModal(
  props: EnableVirtualCardsModalProps,
): ReactElement {
  const {
    className = '',
    isOpen,
    onClose,
    creditCardId,
    onEnableComplete,
    onEnableError,
  } = props

  // STATE
  const [termsExpanded, setTermsExpanded] = useState<boolean>(true)
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const [errorMessage, setErrorMessage] = useState<string | undefined>()
  const [editAccessChecked, setEditAccessChecked] = useState<boolean>(false)
  const [isBlockedByAccountProvider, setIsBlockedByAccountProvider] =
    useState<boolean>(false)

  // SELECTORS
  const user = useSelector(selectUserListModelFromState)

  // API CALLS
  const { mutateAsync: putCreditCardV2 } = usePutCreditCardV2()
  const { data: orgFeatureFlags } = useQueryOrganizationFeatureFlags({
    orgId: user?.organizationId,
  })

  // CUSTOM TOOLS
  const translate = useTranslate()
  const { analytics } = useAnalyticsContext()

  // EVENT HANDLERS
  const handleCheckboxClick = (e: ChangeEvent<HTMLInputElement>) => {
    setEditAccessChecked(e.currentTarget.checked)
  }

  const handleClose = () => {
    analytics.track('cancel enable virtual cards modal')
    // Clear the error message/state if the modal is closed
    if (errorMessage) {
      setErrorMessage(undefined)
    }

    onClose()
    // Reset everything back to normal state, but
    // do so after the modal is closed as not to
    // cause UI jittery ugliness n stuff.
    setTermsExpanded(true)
    setEditAccessChecked(false)
  }

  const handleSubmitData = async () => {
    analytics.track('confirm enable virtual cards modal')
    setIsSubmitting(true)
    try {
      const newAccount = await putCreditCardV2({
        creditCardId: creditCardId,
        endpoint: 'virtual',
      })

      onEnableComplete?.(newAccount?.creditCard?.id)
    } catch (error: unknown) {
      setTermsExpanded(false)
      setIsSubmitting(false)
      const unwrappedError = error as AxiosError

      if (unwrappedError.response?.status === 412) {
        setIsBlockedByAccountProvider(true)
        onEnableError?.()
        return
      }

      //  Casting here as to appease typescript. It is hard to see if there is a generic to send in
      // to appease typescript so I'm casting for the time being
      const errorData = unwrappedError.response?.data as { error?: string }
      if (errorData?.error) {
        setErrorMessage(errorData.error)
      } else {
        const defaultErrorMessage = translate(
          'Card enrollment failed - Try again later',
        )
        setErrorMessage(defaultErrorMessage)
      }
      onEnableError?.()
    } finally {
      setIsSubmitting(false)
    }
  }

  // STATE HELPERS
  const isSubmitDisabled = editAccessChecked === false

  return (
    <BaseModal
      className={cn('EnableVirtualCardsModal', className)}
      variant="popup"
      isOpen={isOpen}
      onClose={handleClose}
      css={style}
      aria-label={translate('Enable Virtual Cards Modal')}
    >
      <div className="flex-column gap-10 container-illustration">
        <img
          src={DiagonalCardsIllustration}
          alt={translate('Diagonal Cards Illustration')}
        />
        <p className="font-h3">
          {translate('Set up your organization for Virtual Cards')}
        </p>
      </div>
      <div className="flex-column">
        <p>
          {translate(
            'Enabling virtual cards allows your organization to issue and manage company spend through Extend. Authorized admins can create, update, or close virtual cards, with all actions logged for visibility and audit history.',
          )}{' '}
          <a
            className="learn-more-text font-linktext"
            target="_blank"
            rel="noreferrer noopener"
            href="https://support.paywithextend.com/hc/en-us/categories/28575621599895"
          >
            {translate('Learn more')}
          </a>
        </p>
        <div className="midsection flex-column">
          <div className="section-acknowledge flex-column gap-5">
            <div className="flex-row justify-between align-center">
              <p className="font-support-medium">
                {translate('Acknowledgements:')}
              </p>
              <Button
                variant="no-shell"
                className="chevron"
                aria-expanded={termsExpanded}
                onClick={() => {
                  setTermsExpanded(!termsExpanded)
                }}
              >
                {termsExpanded ? <SvgChevronDown /> : <SvgChevronUp />}
              </Button>
            </div>
            {termsExpanded && (
              <div className="flex-row gap-3">
                <Checkbox
                  aria-label={translate('Edit Access Acknowledge Checkbox')}
                  data-testid="editAccessAcknowledgeCheckbox"
                  name="editAccess"
                  checked={editAccessChecked}
                  onChange={handleCheckboxClick}
                />
                <div className="flex-column gap-3">
                  <p className="legal-text">
                    <Markdown options={{ disableParsingRawHTML: true }}>
                      {translate(
                        orgFeatureFlags?.cardManagerRoleEnabled
                          ? 'I acknowledge that the Owner and any current or future Admin(s) or Card Manager(s) will have the following edit access over your registered account:'
                          : 'I acknowledge that the Owner and any current or future Admin(s) will have the following edit access over your registered card account: ',
                      )}
                    </Markdown>
                  </p>
                  <ul className="legal-text">
                    <li>
                      <Markdown options={{ disableParsingRawHTML: true }}>
                        {translate(
                          'They can create virtual cards and budgets from your account',
                        )}
                      </Markdown>
                    </li>
                    <li>
                      <Markdown options={{ disableParsingRawHTML: true }}>
                        {translate(
                          'They can close virtual cards and budgets under your account',
                        )}
                      </Markdown>
                    </li>
                    <li>
                      <Markdown options={{ disableParsingRawHTML: true }}>
                        {translate(
                          'They can update virtual card and budget details under your account',
                        )}
                      </Markdown>
                    </li>
                  </ul>

                  <p className="legal-text">
                    {translate(
                      "All actions will be logged in the 'History' of each virtual card or budget, including details of the user who performed the action.",
                    )}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {isBlockedByAccountProvider && (
          <AlertPanel variant="severe">
            <div className="flex-row justify-start gap-4">
              <AlertTriangleIcon />
              <div className="flex-column gap-4">
                <p className="font-bold">
                  {translate(
                    "Virtual card capabilities aren't currently enabled for your bank.",
                  )}
                </p>
                <p>
                  {translate(
                    'To learn more about availability or request access, reach out to your bank representative or ',
                  )}
                  <span
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e: KeyboardEvent) => {
                      if (e.key === 'Enter') {
                        Userpilot.trigger('resource_center:jNfDAOvnub')
                      }
                    }}
                    className="error-link"
                    onClick={() => {
                      Userpilot.trigger('resource_center:jNfDAOvnub')
                    }}
                    rel="noopener noreferrer"
                  >
                    {translate('contact support')}
                  </span>
                </p>
              </div>
            </div>
          </AlertPanel>
        )}
        {errorMessage && (
          <AlertPanel variant="severe">
            <div className="flex-row justify-start gap-4">
              <AlertTriangleIcon />
              <div className="flex-column gap-4">
                <p className="font-bold">
                  {translate('There was an error registering your card')}
                </p>
                <p className="">
                  {errorMessage === 'ERROR_FAILED' ? (
                    <>
                      {translate('Please try again or ')}
                      <span
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e: KeyboardEvent) => {
                          if (e.key === 'Enter') {
                            Userpilot.trigger('resource_center:jNfDAOvnub')
                          }
                        }}
                        className="error-link"
                        onClick={() => {
                          Userpilot.trigger('resource_center:jNfDAOvnub')
                        }}
                        rel="noopener noreferrer"
                      >
                        {translate('contact support')}
                      </span>
                    </>
                  ) : (
                    errorMessage
                  )}
                </p>
              </div>
            </div>
          </AlertPanel>
        )}
        <div className="flex-row justify-end gap-5 button-container">
          <Button
            className="cancel-button"
            variant="no-shell"
            onClick={handleClose}
          >
            {translate('Cancel')}
          </Button>
          <Button
            className="confirm-button"
            data-testid="confirm-button"
            data-isloading={isSubmitting}
            loading={isSubmitting}
            disabled={
              isSubmitting ||
              isSubmitDisabled ||
              !!errorMessage ||
              isBlockedByAccountProvider
            }
            onClick={handleSubmitData}
          >
            {translate('Confirm')}
          </Button>
        </div>
      </div>
    </BaseModal>
  )
}
